import React from 'react';
import './GlossaryTerm.css';

type GlossaryTermProps = {
  term: string;
  definition: string;
  children?: React.ReactNode;
};

/**
 * Inline glossary term with a visible "?" and hover/focus/click tooltip
 * (WCAG 1.4.13-friendly): dismissible (Escape), hoverable, persistent until blur/leave.
 * Use for industry acronyms (E&O, CGL, CMMC, SDN) — not for critical instructions.
 * Do not nest inside another button (accordion triggers).
 */
const GlossaryTerm: React.FC<GlossaryTermProps> = ({ term, definition, children }) => {
  const tooltipId = React.useId();
  const rootRef = React.useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && rootRef.current && !rootRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  return (
    <span
      className={`glossary-term${open ? ' is-open' : ''}`}
      ref={rootRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        const next = event.relatedTarget as Node | null;
        if (next && rootRef.current?.contains(next)) {
          return;
        }
        setOpen(false);
      }}
    >
      <button
        type="button"
        className="glossary-term__trigger"
        aria-describedby={open ? tooltipId : undefined}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <span className="glossary-term__word">{children || term}</span>
        <span className="glossary-term__mark" aria-hidden="true" />
      </button>
      {open ? (
        <span id={tooltipId} className="glossary-term__tooltip" role="tooltip">
          <strong className="glossary-term__term">{term}</strong>
          <span className="glossary-term__definition">{definition}</span>
        </span>
      ) : null}
    </span>
  );
};

export default GlossaryTerm;
